import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import { KYCForm } from 'components/KYCForm';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { KYC as KYCConstants } from 'constants/constants';
import { IsAllDataNull } from 'utils/validation';
import { errorModel, getModel, sendModel } from 'components/KYCForm/models';
import RiskScoreBarChart from 'components/RiskScoreBarChart';
import { ICON_INFO_SLIM } from 'ui-library/components/Icon';
import { dateFormatZurich } from 'utils/datetime';
import { memberProfileSelector } from 'redux-store/auth/authSelectors';
import { useSelector } from 'react-redux';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useQueryClient } from '@tanstack/react-query';
import { KYCDataPayload, KYCErrorData } from 'components/KYCForm/kyc.types';
import { getClientId } from '../../../../utils';
import Preloader from '../../../../components/Preloader';
import Card from '../../../../components/Card/Card';
import ButtonsBlockRow from '../../../../components/ButtonsBlockRow';
import { Icon, Loader, notification } from '../../../../ui-library';
import { useClientLayout } from '../../hooks/useClientLayout';
import ServiceManager from '../../../../services/ServiceManager';

import './ClientKYC.css';

const ClientKYC = () => {
    const { t } = useTranslation();
    const { dfsClientId } = useParams();
    const contactId = getClientId(dfsClientId);
    const [data, setData] = useState<KYCDataPayload>({});
    const [errorData, setErrorData] = useState<KYCErrorData | null>(null);
    const [saveInProgress, setSaveInProgress] = useState(false);

    const queryClient = useQueryClient();
    const memberProfile: any = useSelector(memberProfileSelector);

    const {
        data: lastModifiedMemberProfile,
    } = useGetCustomService({
        service: 'contactManagement',
        api: 'getMembersProfile',
        payload: data?.modifiedBy,
        enabled: !!data?.modifiedBy,
        staleTime: 1000 * 60 * 60 * 12, // 12 hours
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
    });

    const lastModifiedBy = useMemo(() => (lastModifiedMemberProfile ? `${lastModifiedMemberProfile?.data?.FirstName} ${lastModifiedMemberProfile?.data?.LastName}` : ' - '), [lastModifiedMemberProfile]);

    useClientLayout();

    const isBeneficialOwner = useMemo(
        () => data?.beneficialOwner === KYCConstants.thirdParty,
        [data?.beneficialOwner],
    );

    const {
        data: dataContact,
        isLoading: isContactLoading,
        error: contactError,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: contactId,
        enabled: !!contactId,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 2,
    });

    const isSwissNationality = useMemo(() => {
        if (
            dataContact?.data?.details?.personalInformation?.identification?.length
            > 0
        ) {
            return (
                dataContact?.data?.details?.personalInformation?.identification[0]
                    ?.countryOfNationality === KYCConstants.swissNationality
            );
        }

        return false;
    }, [dataContact?.data?.details?.personalInformation]);

    const riskScoreData = useMemo(() => {
        const investmentInfo = dataContact?.data?.details?.investmentInformation?.extRiskScore;

        return {
            riskCategoryCode: investmentInfo?.extRiskCategoryCode,
            riskCategoryChangeDate:
                investmentInfo?.extRiskCategoryChangeDate || '',
            riskCategoryLastResubmissionDate:
                investmentInfo?.extLastResubmissionDate || '',
            riskCategoryNextResubmissionDate:
                investmentInfo?.extNextResubmissionDate || '',
        };
    }, [dataContact?.data?.details?.investmentInformation?.extRiskScore]);

    const staticScores = [
        {
            index: 0,
            className: 'bg-color-normal-risk',
            children: `0 - ${t('clientDashboard.kyc.RiskScore.normal')}`,
        },
        {
            index: 2,
            className: 'bg-color-slightly-increased-risk',
            children: `1 - ${t(
                'clientDashboard.kyc.RiskScore.SlightlyIncreased',
            )}`,
        },
        {
            index: 1,
            className: 'bg-color-increased-risk',
            children: `2+ ${t('clientDashboard.kyc.RiskScore.Increased')}`,
        },
    ];

    useEffect(() => {
        setData(getModel(dataContact?.data?.details?.extKyc));
    }, [dataContact?.data?.details?.extKyc]);

    const handleSubmit = useCallback(async () => {
        const errors = await errorModel(data, isSwissNationality);

        setErrorData(errors);

        if (IsAllDataNull(errors) !== false) {
            const payload = sendModel({
                data: { ...data, modifiedBy: memberProfile?.Id },
            });

            try {
                setSaveInProgress(true);

                await ServiceManager.clientService('patchContact', [
                    contactId,
                    payload,
                ]);

                await ServiceManager.customClientService('postExternalKyc', [
                    { contactId },
                ]);

                queryClient.setQueryData(['getData_getContact', contactId], (old: any) => ({
                    ...old,
                    data: {
                        ...old?.data,
                        details: {
                            ...old?.data?.details,
                            extKyc: payload.details.extKyc,
                        },
                    },
                }));
                notification.open({
                    content: t('general.saveSuccessMessage'),
                    type: 'success',
                });
            } catch (err) {
                notification.open({
                    content: t('general.somethingWentWrong'),
                    type: 'error',
                });
            } finally {
                setSaveInProgress(false);
            }
        }
    }, [data, memberProfile]);

    return (
        <div className="container newStyle ClientKYC mb-40">
            {riskScoreData?.riskCategoryCode != null ? (
                <Card
                    className="mt-20 mb-32"
                    title={t('clientDashboard.kyc.RiskScore.title')}
                    titleTagName="h2"
                >
                    <RiskScoreBarChart
                        activeIndex={riskScoreData?.riskCategoryCode}
                        scores={staticScores}
                    />
                    <div className="riskScore_info">
                        <div className="riskScore_info_item">
                            <div className="riskScore_info_item_label">
                                {t(
                                    'clientDashboard.kyc.RiskScore.LastChangeRiskCategory.label',
                                )}
                            </div>
                            <div className="riskScore_info_item_value">
                                {riskScoreData?.riskCategoryChangeDate
                                    ? moment(
                                        riskScoreData?.riskCategoryChangeDate,
                                    ).format(dateFormatZurich)
                                    : '---'}
                                {' '}
                                <Icon type={ICON_INFO_SLIM} size={20} />
                            </div>
                        </div>
                        <span className="riskScore_info_item_seperator" />
                        <div className="riskScore_info_item">
                            <div className="riskScore_info_item_label">
                                {t(
                                    'clientDashboard.kyc.RiskScore.LastSubmission.label',
                                )}
                            </div>
                            <div className="riskScore_info_item_value">
                                {riskScoreData?.riskCategoryLastResubmissionDate
                                    ? moment(
                                        riskScoreData?.riskCategoryLastResubmissionDate,
                                    ).format(dateFormatZurich)
                                    : '---'}
                                {' '}
                                <Icon type={ICON_INFO_SLIM} size={20} />
                            </div>
                        </div>
                        <span className="riskScore_info_item_seperator" />
                        <div className="riskScore_info_item">
                            <div className="riskScore_info_item_label">
                                {t(
                                    'clientDashboard.kyc.RiskScore.NextSubmission.label',
                                )}
                            </div>
                            <div className="riskScore_info_item_value">
                                {riskScoreData?.riskCategoryNextResubmissionDate
                                    ? moment(
                                        riskScoreData?.riskCategoryNextResubmissionDate,
                                    ).format(dateFormatZurich)
                                    : '---'}
                                {' '}
                                <Icon type={ICON_INFO_SLIM} size={20} />
                            </div>
                        </div>
                    </div>
                </Card>
            ) : (
                ''
            )}
            <Card title={t('clientDashboard.kyc.title')} titleTagName="h1">
                <Preloader isLoading={isContactLoading} error={contactError}>
                    <div className="modification-container">
                        <div className="modification-item">
                            <dt>{t('clientDashboard.kyc.lastModifiedOn')}</dt>
                            <dd>{data?.modifiedDate || '-'}</dd>
                        </div>
                        <div className="modification-item">
                            <dt>{t('clientDashboard.kyc.lastModifiedBy')}</dt>
                            <dd>{lastModifiedBy || '-'}</dd>
                        </div>
                    </div>
                    <KYCForm
                        data={data}
                        personalDetails={dataContact?.data}
                        setData={setData}
                        errorData={errorData}
                        setErrorData={setErrorData}
                        is3B={false}
                    />
                    <ButtonsBlockRow
                        primaryButton={{
                            disabled: isBeneficialOwner || saveInProgress,
                            text: saveInProgress ? (
                                <Loader />
                            ) : (
                                t('clientDashboard.kyc.saveBtn.label')
                            ),
                            onClick: handleSubmit,
                        }}
                    />
                </Preloader>
            </Card>
        </div>
    );
};

export default ClientKYC;
